import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { ACCOUNTING_METHOD_TYPES } from "constants/bookkeeping";
import { ACCOUNTING_METHODS_MAP } from "dictionaries";
import { useToast } from "hooks/useToast";
import {
  useGetBookkeepingSettingsQuery,
  useUpdateBookkeepingSettingsMutation,
} from "store/apis/booksLockDate";
import { BackendError } from "types/utils/error";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";

export const AccountingMethodBanner = ({
  selectedAccountingMethod,
  entityId,
  hasWritePermission: hasWritePermissionProp,
}: {
  selectedAccountingMethod: ACCOUNTING_METHOD_TYPES;
  entityId: string;
  hasWritePermission?: boolean;
}) => {
  const { successToast, alertToast } = useToast();
  const hasAccountingWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.ACCOUNTING
  );
  const hasWritePermission =
    hasWritePermissionProp ?? hasAccountingWritePermission;
  const { data: settings } = useGetBookkeepingSettingsQuery(
    {
      entityId,
    },
    { skip: !entityId }
  );

  const [updateBooksSetting, { isLoading: isUpdating }] =
    useUpdateBookkeepingSettingsMutation();

  const onAccountingMethodChange = async () => {
    if (!entityId) return;

    try {
      await updateBooksSetting({
        entityId,
        accounting_method: selectedAccountingMethod,
      }).unwrap();

      successToast({
        title: "Default Accounting method updated",
        message: `Your default accounting method is now set to ${ACCOUNTING_METHODS_MAP[selectedAccountingMethod]}.`,
      });
    } catch (error) {
      alertToast(
        {
          message:
            (error as BackendError).data?.error?.message ||
            "Failed to update accounting method",
        },
        error as Error
      );
    }
  };

  if (
    settings?.accounting_method &&
    Boolean(selectedAccountingMethod) &&
    settings?.accounting_method !== selectedAccountingMethod
  ) {
    return (
      <div>
        <Banner
          type="info"
          title={`Your default accounting method is ${
            ACCOUNTING_METHODS_MAP[settings?.accounting_method]
          }`}
          description={`The preferred accounting method for this entity is set to ${
            ACCOUNTING_METHODS_MAP[settings?.accounting_method]
          }. If that's not correct, you can update the same.`}
          cta={
            hasWritePermission ? (
              <Button
                customType="secondary"
                type="button"
                onClick={onAccountingMethodChange}
                size="small"
                isLoading={isUpdating}
                disabled={isUpdating}
              >
                Set default to{" "}
                {ACCOUNTING_METHODS_MAP[selectedAccountingMethod]}
              </Button>
            ) : (
              <LockedButton
                text={`Set default to ${ACCOUNTING_METHODS_MAP[selectedAccountingMethod]}`}
              />
            )
          }
        />
      </div>
    );
  }

  return null;
};
